import { LocalStorageHelper, StorageKeys } from '@nice-ccf/core-sdk';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedEditor,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import {
  $applyNodeReplacement,
  createEditor,
  DecoratorNode,
} from 'lexical';

import * as React from 'react';
import { Suspense } from 'react';

/**
 * Lazy load the CcfInlineImageComponent
 */
const CcfInlineImageComponent = React.lazy(
  () => import('./ccf-inline-image-component')
);

/**
 * Define the possible positions for the InlineImage
 */
export type Position = 'left' | 'right' | 'full' | undefined;

/**
 * Interface representing the properties of an Inline Image Payload.
 */
export interface InlineImagePayload {
  altText: string;
  caption?: LexicalEditor;
  height?: number;
  key?: NodeKey;
  showCaption?: boolean;
  src: string;
  width?: number;
  position?: Position;
}

/**
 * Conversion function to convert HTMLImageElement to InlineImageNode
 * @example - convertInlineImageElement
 * @param domNode - HTMLImageElement to convert
 * @returns null if not convertible, DOMConversionOutput otherwise
 */
export function convertInlineImageElement(domNode: Node): null | DOMConversionOutput {
  const img = domNode as HTMLImageElement;
  if (img instanceof HTMLImageElement) {
    //Dev: If image is pasted from local system, then we are not allowing to paste the image as it gives the security issue.
    if (img?.src?.startsWith('file:///')) {
      LocalStorageHelper.setItem(StorageKeys.IMAGE_PASTE_ERROR, 'true');
      return null;
    }
    const { alt: altText, src, width, height } = img;
    const node = $createInlineImageNode({ altText, height, src, width });
    return { node };
  }else {
    return null;
  }
}

/**
 * Define the structure of serialized InlineImageNode
 */
export type SerializedInlineImageNode = Spread<
  {
    altText: string;
    caption: SerializedEditor;
    height?: number;
    showCaption: boolean;
    src: string;
    width?: number;
    position?: Position;
  },
  SerializedLexicalNode
>;

/**
 * InlineImageNode class for handling Inline Image elements
 */
export class InlineImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __width: 'inherit' | number;
  __height: 'inherit' | number;
  __showCaption: boolean;
  __caption: LexicalEditor;
  __position: Position;

  /**
   * Constructor for InlineImageNode
   * @param src - Source of the image.
   * @param altText - Alternative text for the image.
   * @param position - Position of the image.
   * @param width - Width of the image.
   * @param height - Height of the image.
   * @param showCaption - Whether to show the caption.
   * @param caption - LexicalEditor for the caption.
   * @param key - Key of the node.
   * @example - InlineImageNode
   */
  constructor(
    src: string,
    altText: string,
    position: Position,
    width?: 'inherit' | number,
    height?: 'inherit' | number,
    showCaption?: boolean,
    caption?: LexicalEditor,
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width || 'inherit';
    this.__height = height || 'inherit';
    this.__showCaption = showCaption || false;
    this.__caption = caption || createEditor();
    this.__position = position;
  }

  /**
   * Static method to get the type of InlineImageNode
   * @returns 'inline-image'
   * @example - getType
   */
  static getType(): string {
    return 'inline-image';
  }

  /**
   * Static method to clone an InlineImageNode
   * @param node - Node to clone
   * @returns Cloned InlineImageNode
   * @example - clone
   */
  static clone(node: InlineImageNode): InlineImageNode {
    return new InlineImageNode(
      node.__src,
      node.__altText,
      node.__position,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__key
    );
  }

  /**
   * Static method to import InlineImageNode from JSON
   * @param serializedNode - Serialized node to import
   * @returns Imported InlineImageNode
   * @example - importJSON
   */
  static importJSON(serializedNode: SerializedInlineImageNode): InlineImageNode {
    const { altText, height, width, caption, src, showCaption, position } =
      serializedNode;
    const node = $createInlineImageNode({
      altText,
      height,
      position,
      showCaption,
      src,
      width,
    });
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  /**
   * Static method to define DOM conversion for InlineImageNode
   * @returns DOMConversionMap or null
   * @example - importDOM
   */
  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertInlineImageElement,
        priority: 0,
      }),
    };
  }

  /**
   * Export InlineImageNode to DOM
   * @returns DOMExportOutput
   * @example - exportDOM
   */
  exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    element.setAttribute('width', this.__width.toString());
    element.setAttribute('height', this.__height.toString());
    return { element };
  }

  /**
   * Export InlineImageNode to JSON
   * @returns SerializedInlineImageNode
   * @example - exportJSON
   */
  exportJSON(): SerializedInlineImageNode {
    return {
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height === 'inherit' ? 0 : this.__height,
      position: this.__position,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      type: 'inline-image',
      version: 1,
      width: this.__width === 'inherit' ? 0 : this.__width,
    };
  }

  /**
   * Getter for src property
   * @returns Source of the image
   * @example - getSrc
   */
  getSrc(): string {
    return this.__src;
  }

  /**
   * Getter for altText property
   * @returns Alternative text for the image
   * @example - getAltText
   */
  getAltText(): string {
    return this.__altText;
  }

  /**
   * Setter for altText property
   * @param altText - New alternative text
   * @example - setAltText
   */
  setAltText(altText: string): void {
    const writable = this.getWritable();
    writable.__altText = altText;
  }

  /**
   * Setter for width and height properties
   * @param width - New width of the image
   * @param height - New height of the image
   * @example - setWidthAndHeight
   */
  setWidthAndHeight(
    width: 'inherit' | number,
    height: 'inherit' | number
  ): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  /**
   * Getter for showCaption property
   * @returns Whether to show the caption
   * @example - getShowCaption
   */
  getShowCaption(): boolean {
    return this.__showCaption;
  }

  /**
   * Setter for showCaption property
   * @param showCaption - Whether to show the caption
   * @example - setShowCaption
   */
  setShowCaption(showCaption: boolean): void {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  /**
   * Getter for position property
   * @returns Position of the image
   * @example - getPosition
   */
  getPosition(): Position {
    return this.__position;
  }

  /**
   * Setter for position property
   * @param position - New position of the image
   * @example - setPosition
   */
  setPosition(position: Position): void {
    const writable = this.getWritable();
    writable.__position = position;
  }

  /**
   * Create DOM element for InlineImageNode
   * @param config - Editor configuration
   * @returns Created DOM element
   * @example - createDOM
   */
  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const className = `${config.theme.inlineImage} position-${this.__position}`;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  /**
   * Update DOM element for InlineImageNode
   * @param prevNode - Previous state of the node
   * @param dom - Current DOM element
   * @param config - Editor configuration
   * @returns false
   * @example - updateDOM
   */
  updateDOM(
    prevNode: InlineImageNode,
    dom: HTMLElement,
    config: EditorConfig
  ): false {
    const position = this.__position;
    if (position !== prevNode.__position) {
      const className = `${config.theme.inlineImage} position-${position}`;
      if (className !== undefined) {
        dom.className = className;
      }
    }
    return false;
  }

  /**
   * Decorate method for rendering InlineImageNode
   * @returns JSX element for rendering
   * @example - decorate
   */
  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <CcfInlineImageComponent
          src={this.__src}
          altText={this.__altText}
          width={this.__width}
          height={this.__height}
          nodeKey={this.getKey()}
          showCaption={this.__showCaption}
          caption={this.__caption}
          position={this.__position}
        />
      </Suspense>
    );
  }
}

/**
 * Factory function to create InlineImageNode
 * @param payload - Payload for creating InlineImageNode
 * @returns Created InlineImageNode
 * @example - $createInlineImageNode
 */
export function $createInlineImageNode({
  altText,
  position,
  height,
  src,
  width,
  showCaption,
  caption,
  key,
}: InlineImagePayload): InlineImageNode {
  return $applyNodeReplacement(
    new InlineImageNode(
      src,
      altText,
      position,
      width,
      height,
      showCaption,
      caption,
      key
    )
  );
}

/**
 * Function to check if a node is an InlineImageNode
 * @param node - Node to check
 * @returns Whether the node is an InlineImageNode
 * @example - $isInlineImageNode
 */
export function $isInlineImageNode(
  node: LexicalNode | null | undefined
): node is InlineImageNode {
  return node instanceof InlineImageNode;
}